import { useEffect, useState, type ComponentProps } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, consumeServerStatusMessage, consumeSessionExpiredMessage } from '../api/client';
import accountIcon from '../assets/sidebar/Account.svg';
import logoImage from '../assets/runity-logo.svg';
import passwordIcon from '../assets/sidebar/Password.svg';
import { LOGIN_VERIFY_ROUTE_PATH } from '../routes/appRoutes';
import { login, sendPasswordResetEmail } from '../services/authService';

const REMEMBERED_ACCOUNT_STORAGE_KEY = 'rememberedAccountId';
const SESSION_EXPIRED_MESSAGE = '세션이 만료되었습니다. 다시 로그인해주세요.';

function getLoginErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    return error.message;
  }

  return '로그인 처리 중 오류가 발생했습니다.';
}

function isValidEmail(email: string) {
  const [localPart, domainPart, ...extraParts] = email.split('@');

  if (!localPart || !domainPart || extraParts.length > 0) {
    return false;
  }

  const domainLabels = domainPart.split('.');

  return domainLabels.length >= 2 && domainLabels.every((label) => label.length > 0);
}

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountId, setAccountId] = useState(
    () => localStorage.getItem(REMEMBERED_ACCOUNT_STORAGE_KEY) ?? ''
  );
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState(() => {
    const sessionExpiredMessage = consumeSessionExpiredMessage();
    if (sessionExpiredMessage) {
      return sessionExpiredMessage;
    }
    const serverStatusMessage = consumeServerStatusMessage();
    if (serverStatusMessage) {
      return serverStatusMessage;
    }
    return searchParams.get('reason') === 'session_expired' ? SESSION_EXPIRED_MESSAGE : '';
  });
  const [isRememberId, setIsRememberId] = useState(
    () => Boolean(localStorage.getItem(REMEMBERED_ACCOUNT_STORAGE_KEY))
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetErrorMessage, setResetErrorMessage] = useState('');
  const [resetNoticeMessage, setResetNoticeMessage] = useState('');
  const [isResetSending, setIsResetSending] = useState(false);

  useEffect(() => {
    if (searchParams.get('reason')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLogin = async () => {
    setErrorMessage('');

    const account = accountId.trim();

    if (!account || !password.trim()) {
      setErrorMessage('아이디와 비밀번호를 모두 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      await login({
        account,
        password,
      });
      if (isRememberId) {
        localStorage.setItem(REMEMBERED_ACCOUNT_STORAGE_KEY, account);
      } else {
        localStorage.removeItem(REMEMBERED_ACCOUNT_STORAGE_KEY);
      }
      navigate(LOGIN_VERIFY_ROUTE_PATH);
    } catch (error) {
      setErrorMessage(getLoginErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();
    void submitLogin();
  };

  const handleRememberIdChange: ComponentProps<'input'>['onChange'] = (event) => {
    const checked = event.target.checked;
    setIsRememberId(checked);
    if (!checked) {
      localStorage.removeItem(REMEMBERED_ACCOUNT_STORAGE_KEY);
    }
  };

  const closePasswordResetModal = () => {
    if (isResetSending) {
      return;
    }

    setIsPasswordResetModalOpen(false);
    setResetEmail('');
    setResetErrorMessage('');
    setResetNoticeMessage('');
  };

  const submitPasswordResetEmail = async () => {
    setResetErrorMessage('');
    setResetNoticeMessage('');

    const email = resetEmail.trim();

    if (!email) {
      setResetErrorMessage('이메일을 입력해주세요.');
      return;
    }

    if (!isValidEmail(email)) {
      setResetErrorMessage('올바른 이메일 형식으로 입력해주세요.');
      return;
    }

    setIsResetSending(true);

    try {
      await sendPasswordResetEmail(email);
      setResetNoticeMessage('비밀번호 재설정 메일을 발송했습니다.');
    } catch (error) {
      setResetErrorMessage(getLoginErrorMessage(error));
    } finally {
      setIsResetSending(false);
    }
  };

  const handlePasswordResetSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();
    void submitPasswordResetEmail();
  };

  return (
    <main className="auth-page">
      <section className="auth-panel login-panel" aria-labelledby="login-title">
        <img className="login-logo" src={logoImage} alt="RUNITY" />

        <div className="login-heading">
          <h1 id="login-title">로그인</h1>
          <p>로그인 정보를 입력해주세요</p>
        </div>

        <form className="auth-form login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            <img className="login-field-icon" src={accountIcon} alt="" aria-hidden="true" />
            <span className="sr-only">아이디</span>
            <input
              type="text"
              aria-label="아이디"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              placeholder="아이디를 입력하세요"
              autoComplete="username"
            />
          </label>

          <label className="login-field">
            <img className="login-field-icon" src={passwordIcon} alt="" aria-hidden="true" />
            <span className="sr-only">비밀번호</span>
            <input
              type="password"
              aria-label="비밀번호"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="비밀번호를 입력하세요"
              autoComplete="current-password"
            />
          </label>

          <div className="login-options">
            <label className="remember-id">
              <input
                type="checkbox"
                checked={isRememberId}
                onChange={handleRememberIdChange}
              />
              <span>아이디 기억하기</span>
            </label>
            <button
              className="forgot-password"
              type="button"
              onClick={() => setIsPasswordResetModalOpen(true)}
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>

          {errorMessage && <p className="auth-error">{errorMessage}</p>}

          <button className="auth-submit login-submit" type="submit" disabled={isLoading}>
            {isLoading ? '로그인 중...' : '로그인'}
          </button>
        </form>
      </section>

      {isPasswordResetModalOpen && (
        <dialog
          className="password-reset-backdrop"
          open
          aria-labelledby="password-reset-title"
          onCancel={closePasswordResetModal}
        >
          <button
            className="password-reset-backdrop-button"
            type="button"
            aria-label="비밀번호 찾기 모달 닫기"
            disabled={isResetSending}
            onClick={closePasswordResetModal}
          />
          <section className="password-reset-modal">
            <div className="password-reset-heading">
              <h2 id="password-reset-title">비밀번호 찾기</h2>
              <p>가입한 이메일로 비밀번호 재설정 메일을 발송합니다.</p>
            </div>

            <form className="password-reset-form" onSubmit={handlePasswordResetSubmit}>
              <label className="password-reset-field">
                <span>이메일</span>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(event) => setResetEmail(event.target.value)}
                  placeholder="이메일을 입력하세요"
                  autoComplete="email"
                />
              </label>

              {resetErrorMessage && <p className="auth-error">{resetErrorMessage}</p>}
              {resetNoticeMessage && <p className="auth-notice">{resetNoticeMessage}</p>}

              <div className="password-reset-actions">
                <button
                  className="auth-secondary-button password-reset-cancel"
                  type="button"
                  disabled={isResetSending}
                  onClick={closePasswordResetModal}
                >
                  취소
                </button>
                <button className="auth-submit password-reset-submit" type="submit" disabled={isResetSending}>
                  {isResetSending ? '발송 중...' : '메일 발송'}
                </button>
              </div>
            </form>
          </section>
        </dialog>
      )}
    </main>
  );
}

export default LoginPage;
